import { z } from "zod";
import {
  ATTRIBUTION_CHANNELS,
  CANCEL_REASONS,
  CHANNELS,
  CONDITION_OPERATORS,
  CONFIRMATION_STATES,
  CONVERSATION_STATUSES,
  CUSTOMER_SOURCES,
  FOLLOWUP_STATUSES,
  FOLLOWUP_TYPES,
  MESSAGE_TYPES,
  ORDER_SOURCES,
  ORDER_STATUSES,
  PAYMENT_METHODS,
  PLAYBOOK_TRIGGERS,
  SHIPMENT_STATUSES,
  AI_MODES,
} from "../enums";
import type { ConditionOperator, FollowupStatus, FollowupType, MessageType, PlaybookAction, PlaybookTrigger } from "../enums";
import {
  ATTRIBUTION_CHANNEL_LABELS,
  CANCEL_REASON_LABELS,
  CHANNEL_LABELS,
  CONFIRMATION_STATE_LABELS,
  CONVERSATION_STATUS_LABELS,
  CUSTOMER_SOURCE_LABELS,
  ORDER_SOURCE_LABELS,
  ORDER_STATUS_LABELS,
  PAYMENT_METHOD_LABELS,
  SHIPMENT_STATUS_LABELS,
} from "../labels";
import { AI_MODE_LABELS } from "./conversations";
import { RFM_SEGMENTS, RFM_SEGMENT_LABELS, RISK_ACTIONS, RISK_ACTION_LABELS, RISK_LEVELS, RISK_LEVEL_LABELS, type RfmSegment } from "./growth";

const MESSAGE_TYPE_LABELS: Record<MessageType, string> = {
  text: "Texte",
  image: "Image",
  video: "Vidéo",
  audio: "Message vocal",
  file: "Fichier",
  template: "Modèle WhatsApp",
  interactive: "Réponse à un bouton",
  location: "Position partagée",
  sticker: "Autocollant",
  reaction: "Réaction",
  note: "Note interne",
  unsupported: "Non pris en charge",
};

// Automation (Relances and Playbooks): rules, follow-ups, playbooks, the condition field catalog shared by the API
// engine and the web builder, and the message variables. Texts are French, sanitized on the server before sending.

const id = z.string().regex(/^[a-f0-9]{24}$/i, "Identifiant invalide");
const hhmm = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, "Heure invalide (HH:MM)");

// ---------------------------------------------------------------------------------------------------------------
// Follow-ups (Relances)

/** Follow-up types a merchant can create rules for (order_confirmation belongs to the confirmation orchestrator). */
export const FOLLOWUP_RULE_TYPES = [
  "unconfirmed_order",
  "abandoned_form",
  "failed_delivery",
  "shipping_update",
  "review_request",
  "replenishment",
  "win_back",
  "payment_reminder",
  "custom",
] as const satisfies readonly FollowupType[];
export type FollowupRuleType = (typeof FOLLOWUP_RULE_TYPES)[number];

export const FOLLOWUP_CHANNELS = ["auto", "whatsapp", "messenger", "instagram"] as const;
export type FollowupChannel = (typeof FOLLOWUP_CHANNELS)[number];

export const FOLLOWUP_CHANNEL_LABELS: Record<FollowupChannel, string> = {
  auto: "Meilleur canal disponible",
  whatsapp: "WhatsApp",
  messenger: "Messenger",
  instagram: "Instagram",
};

/** WhatsApp template category of a flow: marketing needs consent, caps and a holdout; utility does not. */
export const MESSAGE_CATEGORIES = ["utility", "marketing"] as const;
export type MessageCategory = (typeof MESSAGE_CATEGORIES)[number];

export const MESSAGE_CATEGORY_LABELS: Record<MessageCategory, string> = {
  utility: "Transactionnel",
  marketing: "Marketing",
};

export const FOLLOWUP_DEFAULT_CATEGORY: Record<FollowupType, MessageCategory> = {
  order_confirmation: "utility",
  unconfirmed_order: "utility",
  abandoned_form: "marketing",
  failed_delivery: "utility",
  shipping_update: "utility",
  review_request: "utility",
  replenishment: "marketing",
  win_back: "marketing",
  payment_reminder: "utility",
  custom: "marketing",
};

/** When a rule of each type starts its sequence (shown in the rule editor). */
export const FOLLOWUP_TRIGGER_HINTS: Record<FollowupRuleType, string> = {
  unconfirmed_order: "À la création d'une commande qui n'est pas encore confirmée. S'arrête dès que la commande est confirmée ou annulée.",
  abandoned_form: "Quand un client commence un formulaire avec son téléphone et son accord, sans l'envoyer.",
  failed_delivery: "Quand le transporteur signale une tentative de livraison échouée.",
  shipping_update: "Quand le colis part en livraison chez le client.",
  review_request: "Après la livraison de la commande.",
  replenishment: "Après la livraison, selon la durée de consommation du produit quand elle est renseignée.",
  win_back: "Quand un client dépasse son rythme d'achat habituel (calcul quotidien).",
  payment_reminder: "Quand un lien de paiement est envoyé et reste impayé.",
  custom: "Uniquement depuis un playbook ou un envoi manuel.",
};

/** Reasons recorded on skipped or cancelled follow-ups. */
export const FOLLOWUP_SKIP_REASONS = [
  "opted_out",
  "no_consent",
  "customer_blocked",
  "no_customer",
  "holdout",
  "daily_cap",
  "weekly_cap",
  "monthly_cap",
  "low_engagement",
  "negative_ev",
  "order_moved_on",
  "stop_on_reply",
  "already_converted",
  "rule_disabled",
  "bot_confirmation",
  "expired",
  "cancelled_by_user",
] as const;
export type FollowupSkipReason = (typeof FOLLOWUP_SKIP_REASONS)[number];

export const FOLLOWUP_SKIP_REASON_LABELS: Record<FollowupSkipReason, string> = {
  opted_out: "Client désinscrit (STOP)",
  no_consent: "Pas d'accord marketing du client",
  customer_blocked: "Client bloqué",
  no_customer: "Client introuvable",
  holdout: "Groupe témoin (aucun envoi, mesure de l'impact)",
  daily_cap: "Limite quotidienne de messages marketing",
  weekly_cap: "Limite hebdomadaire de messages atteinte",
  monthly_cap: "Limite mensuelle de messages marketing",
  low_engagement: "Messages précédents non lus, pause de 30 jours",
  negative_ev: "Coût du message supérieur au gain attendu",
  order_moved_on: "La commande a évolué entre-temps",
  stop_on_reply: "Le client a répondu",
  already_converted: "Le client a déjà commandé",
  rule_disabled: "Règle désactivée ou supprimée",
  bot_confirmation: "La confirmation automatique s'en charge",
  expired: "Envoi trop tardif, abandonné",
  cancelled_by_user: "Annulée par un membre de l'équipe",
};

/** Variables available in follow-up and playbook texts ({{customer.first_name}}). */
export const MESSAGE_VARIABLES = [
  { key: "customer.first_name", label: "Prénom du client" },
  { key: "customer.name", label: "Nom du client" },
  { key: "order.number", label: "Numéro de commande" },
  { key: "order.total", label: "Montant de la commande" },
  { key: "order.cod_amount", label: "Montant à payer à la livraison" },
  { key: "order.items", label: "Articles commandés" },
  { key: "order.city", label: "Ville de livraison" },
  { key: "order.delivery_date", label: "Date de livraison prévue" },
  { key: "order.tracking_number", label: "Numéro de suivi" },
  { key: "order.tracking_url", label: "Lien de suivi" },
  { key: "product.name", label: "Produit" },
  { key: "product.stock", label: "Stock du produit" },
  { key: "shop.name", label: "Nom de la boutique" },
  { key: "incentive", label: "Incitation de l'étape" },
] as const;
export type MessageVariableKey = (typeof MESSAGE_VARIABLES)[number]["key"];

const conditionValue = z.union([z.string().max(500), z.number(), z.boolean(), z.array(z.union([z.string().max(200), z.number()])).max(50), z.null()]);

export const conditionInputSchema = z.object({
  field: z
    .string()
    .trim()
    .min(1, "Champ requis")
    .max(120)
    .regex(/^[a-zA-Z0-9_.]+$/, "Champ invalide"),
  op: z.enum(CONDITION_OPERATORS),
  value: conditionValue.optional().default(null),
});
export type ConditionInput = z.infer<typeof conditionInputSchema>;

export const followupStepSchema = z
  .object({
    delayMinutes: z.number().int("Délai invalide").min(0, "Délai invalide").max(525_600, "Un an maximum"),
    messageBody: z.string().trim().max(1000, "1 000 caractères maximum").optional().default(""),
    templateId: id.nullable().optional().default(null),
    incentive: z.string().trim().max(200, "200 caractères maximum").optional().default(""),
  })
  .refine((s) => Boolean(s.messageBody) || Boolean(s.templateId), { message: "Écrivez un message ou choisissez un modèle WhatsApp", path: ["messageBody"] });
export type FollowupStepInput = z.infer<typeof followupStepSchema>;

const followupRuleFields = {
  name: z.string().trim().min(2, "Nom trop court").max(120),
  type: z.enum(FOLLOWUP_RULE_TYPES, { errorMap: () => ({ message: "Type de relance invalide" }) }),
  enabled: z.boolean().optional().default(true),
  category: z.enum(MESSAGE_CATEGORIES).optional(),
  channel: z.enum(FOLLOWUP_CHANNELS).optional().default("auto"),
  steps: z.array(followupStepSchema).min(1, "Ajoutez au moins une étape").max(5, "5 étapes maximum"),
  stopOnReply: z.boolean().optional().default(true),
  sendTimeOptimization: z.boolean().optional().default(false),
  quietHours: z.object({ start: hhmm, end: hhmm }).optional().default({ start: "21:30", end: "09:00" }),
  maxPerCustomerPerWeek: z.number().int().min(1, "Minimum 1").max(21, "Maximum 21").optional().default(3),
  holdoutPercent: z.number().int().min(0, "Minimum 0").max(50, "Maximum 50").optional(),
  attributionWindowHours: z.number().int().min(1).max(720, "30 jours maximum").optional().default(72),
  conditions: z.array(conditionInputSchema).max(10, "10 conditions maximum").optional().default([]),
};

export const followupRuleInputSchema = z.object(followupRuleFields);
export type FollowupRuleInput = z.infer<typeof followupRuleInputSchema>;

export const followupRuleUpdateSchema = z
  .object({
    name: followupRuleFields.name,
    enabled: z.boolean(),
    category: z.enum(MESSAGE_CATEGORIES),
    channel: z.enum(FOLLOWUP_CHANNELS),
    steps: followupRuleFields.steps,
    stopOnReply: z.boolean(),
    sendTimeOptimization: z.boolean(),
    quietHours: z.object({ start: hhmm, end: hhmm }),
    maxPerCustomerPerWeek: z.number().int().min(1).max(21),
    holdoutPercent: z.number().int().min(0).max(50),
    attributionWindowHours: z.number().int().min(1).max(720),
    conditions: z.array(conditionInputSchema).max(10),
  })
  .partial();
export type FollowupRuleUpdate = z.infer<typeof followupRuleUpdateSchema>;

export const followupListQuery = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(25),
  status: z.enum([...FOLLOWUP_STATUSES, "all"] as const).default("all"),
  type: z.enum(FOLLOWUP_TYPES).optional(),
  ruleId: id.optional(),
  customerId: id.optional(),
  orderId: id.optional(),
});
export type FollowupListQuery = z.infer<typeof followupListQuery>;

export const followupStatsQuery = z.object({
  days: z.coerce.number().int().min(1).max(365).default(30),
});
export type FollowupStatsQuery = z.infer<typeof followupStatsQuery>;

export const manualFollowupSchema = z
  .object({
    target: z.discriminatedUnion("kind", [
      z.object({ kind: z.literal("customer"), customerId: id }),
      z.object({ kind: z.literal("segment"), segment: z.enum(RFM_SEGMENTS) }),
    ]),
    category: z.enum(MESSAGE_CATEGORIES).optional().default("marketing"),
    channel: z.enum(FOLLOWUP_CHANNELS).optional().default("auto"),
    templateId: id.nullable().optional().default(null),
    templateVariables: z.array(z.string().trim().max(200)).max(20).optional().default([]),
    messageBody: z.string().trim().max(1000, "1 000 caractères maximum").optional().default(""),
    scheduledAt: z.string().datetime({ offset: true }).nullable().optional(),
    applyHoldout: z.boolean().optional().default(true),
  })
  .superRefine((v, ctx) => {
    if (v.target.kind === "segment" && !v.templateId) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["templateId"], message: "Un envoi à un segment utilise un modèle WhatsApp approuvé" });
    }
    if (v.target.kind === "customer" && !v.templateId && !v.messageBody) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["messageBody"], message: "Écrivez un message ou choisissez un modèle WhatsApp" });
    }
  });
export type ManualFollowupInput = z.infer<typeof manualFollowupSchema>;

export interface FollowupStepDto {
  delayMinutes: number;
  messageBody: string;
  templateId: string | null;
  templateName: string | null;
  incentive: string;
}

export interface FollowupRuleStatsDto {
  scheduled: number;
  sent: number;
  failed: number;
  skipped: number;
  cancelled: number;
  replied: number;
  converted: number;
  revenueMinor: number;
  holdout: number;
  holdoutConverted: number;
  replyRate: number | null;
  conversionRate: number | null;
  holdoutConversionRate: number | null;
  /** Conversion rate of the treated group minus the holdout group (null below 30 holdout customers). */
  incrementalLift: number | null;
}

export interface FollowupRuleDto {
  id: string;
  name: string;
  type: FollowupRuleType;
  typeLabel: string;
  triggerHint: string;
  category: MessageCategory;
  enabled: boolean;
  channel: FollowupChannel;
  steps: FollowupStepDto[];
  stopOnReply: boolean;
  sendTimeOptimization: boolean;
  quietHours: { start: string; end: string };
  maxPerCustomerPerWeek: number;
  holdoutPercent: number;
  attributionWindowHours: number;
  conditions: ConditionInput[];
  presetKey: string | null;
  stats: FollowupRuleStatsDto;
  createdAt: string;
  updatedAt: string;
}

export interface FollowupDto {
  id: string;
  ruleId: string | null;
  ruleName: string | null;
  type: FollowupType;
  typeLabel: string;
  category: MessageCategory;
  step: number;
  stepCount: number;
  status: FollowupStatus;
  channel: string;
  customer: { id: string | null; name: string; phone: string | null };
  orderId: string | null;
  orderNumber: string | null;
  conversationId: string | null;
  scheduledAt: string;
  sentAt: string | null;
  repliedAt: string | null;
  convertedAt: string | null;
  revenueMinor: number;
  reason: string | null;
  reasonLabel: string | null;
  error: string | null;
  holdout: boolean;
  manual: boolean;
  text: string;
  createdAt: string;
}

export interface FollowupStatsDto {
  currency: string;
  days: number;
  current: { sent: number; replied: number; converted: number; revenueMinor: number; failed: number; skipped: number; scheduled: number; holdout: number; holdoutConverted: number };
  previous: { sent: number; replied: number; converted: number; revenueMinor: number };
  replyRate: number | null;
  conversionRate: number | null;
  incrementalLift: number | null;
  daily: Array<{ date: string; sent: number; converted: number }>;
  rules: Array<{ ruleId: string; name: string; type: FollowupRuleType; stats: FollowupRuleStatsDto }>;
}

export interface FollowupPresetDto {
  key: string;
  name: string;
  type: FollowupRuleType;
  category: MessageCategory;
  description: string;
  steps: Array<{ delayMinutes: number; messageBody: string; incentive: string }>;
  holdoutPercent: number;
  added: boolean;
}

export interface ManualFollowupResultDto {
  scheduled: number;
  holdout: number;
  excluded: { noConsent: number; optedOut: number; blocked: number; noContact: number };
  total: number;
  scheduledAt: string;
}

export interface ApprovedTemplateDto {
  id: string;
  name: string;
  language: string;
  category: "MARKETING" | "UTILITY" | "AUTHENTICATION";
  purpose: string;
  body: string;
  variables: string[];
  sendable: boolean;
}

// ---------------------------------------------------------------------------------------------------------------
// Playbooks

export const PLAYBOOK_RUN_STATUSES = ["running", "waiting", "completed", "failed", "skipped"] as const;
export type PlaybookRunStatus = (typeof PLAYBOOK_RUN_STATUSES)[number];

export const PLAYBOOK_RUN_STATUS_LABELS: Record<PlaybookRunStatus, string> = {
  running: "En cours",
  waiting: "En attente",
  completed: "Terminé",
  failed: "Échec",
  skipped: "Ignoré",
};

export const PLAYBOOK_STEP_STATUS_LABELS: Record<"completed" | "failed" | "skipped", string> = {
  completed: "Effectuée",
  failed: "Échec",
  skipped: "Ignorée",
};

export const CONDITION_OPERATOR_LABELS: Record<ConditionOperator, string> = {
  eq: "est égal à",
  neq: "est différent de",
  gt: "est supérieur à",
  gte: "est supérieur ou égal à",
  lt: "est inférieur à",
  lte: "est inférieur ou égal à",
  contains: "contient",
  not_contains: "ne contient pas",
  in: "est l'un de",
  not_in: "n'est aucun de",
  exists: "est renseigné",
  not_exists: "n'est pas renseigné",
};

export const ACTION_TARGETS = ["order", "customer", "conversation"] as const;
export type ActionTarget = (typeof ACTION_TARGETS)[number];

export const ACTION_TARGET_LABELS: Record<ActionTarget, string> = { order: "Commande", customer: "Client", conversation: "Conversation" };

export const PLAYBOOK_ORDER_STATUSES = ["confirmed", "preparing", "shipped", "delivered", "returned", "cancelled"] as const;
export type PlaybookOrderStatus = (typeof PLAYBOOK_ORDER_STATUSES)[number];

export const ASSIGN_MODES = ["user", "round_robin", "unassign"] as const;
export type AssignMode = (typeof ASSIGN_MODES)[number];

export const ASSIGN_MODE_LABELS: Record<AssignMode, string> = {
  user: "Un membre précis",
  round_robin: "Le membre le moins chargé",
  unassign: "Retirer l'assignation",
};

/** Entities loaded in the context of each trigger (the builder only offers fields of these groups). */
export const TRIGGER_ENTITIES: Record<PlaybookTrigger, Array<"order" | "customer" | "conversation" | "shipment" | "product" | "event" | "message">> = {
  "order.created": ["order", "customer", "conversation", "event"],
  "order.confirmed": ["order", "customer", "conversation", "event"],
  "order.cancelled": ["order", "customer", "conversation", "event"],
  "order.status_changed": ["order", "customer", "conversation", "shipment", "event"],
  "shipment.status_changed": ["order", "customer", "conversation", "shipment", "event"],
  "message.received": ["message", "conversation", "customer", "order", "event"],
  "conversation.created": ["conversation", "customer", "order", "event"],
  "lead.created": ["customer", "event"],
  "customer.created": ["customer", "event"],
  "form.abandoned": ["customer", "event"],
  "product.low_stock": ["product", "event"],
  "ads.alert": ["event"],
  "schedule.daily": [],
};

/** Actions that need an entity of the context (others work with every trigger). */
export const ACTION_REQUIREMENTS: Partial<Record<PlaybookAction, "order" | "customer" | "conversation">> = {
  send_message: "customer",
  send_template: "customer",
  ai_reply: "conversation",
  create_shipment: "order",
  set_order_status: "order",
  request_confirmation: "order",
  assign_conversation: "conversation",
  set_conversation_status: "conversation",
  create_followup: "customer",
};

export type PlaybookFieldType = "text" | "number" | "money" | "boolean" | "enum" | "tags";

export interface PlaybookFieldDef {
  path: string;
  label: string;
  group: "order" | "customer" | "conversation" | "shipment" | "product" | "event" | "message";
  type: PlaybookFieldType;
  options?: Array<{ value: string; label: string }>;
  /** Event fields only exist for these triggers. */
  triggers?: PlaybookTrigger[];
}

const opts = <T extends string>(values: readonly T[], labels: Record<T, string>) => values.map((v) => ({ value: v, label: labels[v] }));

export const PLAYBOOK_FIELDS: PlaybookFieldDef[] = [
  { path: "order.status", label: "Statut de la commande", group: "order", type: "enum", options: opts(ORDER_STATUSES, ORDER_STATUS_LABELS) },
  { path: "order.totalMinor", label: "Montant de la commande", group: "order", type: "money" },
  { path: "order.itemsCount", label: "Nombre d'articles", group: "order", type: "number" },
  { path: "order.productNames", label: "Produits commandés", group: "order", type: "tags" },
  { path: "order.source", label: "Source de la commande", group: "order", type: "enum", options: opts(ORDER_SOURCES, ORDER_SOURCE_LABELS) },
  { path: "order.paymentMethod", label: "Mode de paiement", group: "order", type: "enum", options: opts(PAYMENT_METHODS, PAYMENT_METHOD_LABELS) },
  { path: "order.confirmationState", label: "État de confirmation", group: "order", type: "enum", options: opts(CONFIRMATION_STATES, CONFIRMATION_STATE_LABELS) },
  { path: "order.riskLevel", label: "Niveau de risque de retour", group: "order", type: "enum", options: opts(RISK_LEVELS, RISK_LEVEL_LABELS) },
  { path: "order.riskAction", label: "Action recommandée (risque)", group: "order", type: "enum", options: opts(RISK_ACTIONS, RISK_ACTION_LABELS) },
  { path: "order.riskScore", label: "Score de risque (0 à 100)", group: "order", type: "number" },
  { path: "order.governorate", label: "Gouvernorat de livraison", group: "order", type: "text" },
  { path: "order.city", label: "Ville de livraison", group: "order", type: "text" },
  { path: "order.channel", label: "Canal d'acquisition", group: "order", type: "enum", options: opts(ATTRIBUTION_CHANNELS, ATTRIBUTION_CHANNEL_LABELS) },
  { path: "order.tags", label: "Tags de la commande", group: "order", type: "tags" },
  { path: "order.ageMinutes", label: "Âge de la commande (minutes)", group: "order", type: "number" },
  { path: "customer.tags", label: "Tags du client", group: "customer", type: "tags" },
  { path: "customer.ordersCount", label: "Nombre de commandes du client", group: "customer", type: "number" },
  { path: "customer.deliveredCount", label: "Commandes livrées du client", group: "customer", type: "number" },
  { path: "customer.returnedCount", label: "Retours du client", group: "customer", type: "number" },
  { path: "customer.totalSpentMinor", label: "Total dépensé par le client", group: "customer", type: "money" },
  { path: "customer.segment", label: "Segment RFM", group: "customer", type: "enum", options: opts(RFM_SEGMENTS, RFM_SEGMENT_LABELS as Record<RfmSegment, string>) },
  { path: "customer.riskLevel", label: "Risque du client", group: "customer", type: "enum", options: opts(RISK_LEVELS, RISK_LEVEL_LABELS) },
  { path: "customer.marketingConsent", label: "Accord marketing", group: "customer", type: "boolean" },
  { path: "customer.optedOut", label: "Désinscrit des messages", group: "customer", type: "boolean" },
  { path: "customer.daysSinceLastOrder", label: "Jours depuis la dernière commande", group: "customer", type: "number" },
  { path: "customer.governorate", label: "Gouvernorat du client", group: "customer", type: "text" },
  { path: "customer.source", label: "Source du client", group: "customer", type: "enum", options: opts(CUSTOMER_SOURCES, CUSTOMER_SOURCE_LABELS) },
  { path: "conversation.channel", label: "Canal de la conversation", group: "conversation", type: "enum", options: opts(CHANNELS, CHANNEL_LABELS) },
  { path: "conversation.status", label: "Statut de la conversation", group: "conversation", type: "enum", options: opts(CONVERSATION_STATUSES, CONVERSATION_STATUS_LABELS) },
  { path: "conversation.tags", label: "Tags de la conversation", group: "conversation", type: "tags" },
  { path: "conversation.aiMode", label: "Mode IA de la conversation", group: "conversation", type: "enum", options: opts(AI_MODES, AI_MODE_LABELS) },
  { path: "conversation.assigned", label: "Conversation assignée", group: "conversation", type: "boolean" },
  { path: "conversation.leadScore", label: "Score prospect (0 à 100)", group: "conversation", type: "number" },
  { path: "conversation.fromAd", label: "Conversation venue d'une publicité", group: "conversation", type: "boolean" },
  { path: "message.text", label: "Texte du message", group: "message", type: "text" },
  { path: "message.type", label: "Type de message", group: "message", type: "enum", options: opts(MESSAGE_TYPES, MESSAGE_TYPE_LABELS) },
  { path: "shipment.status", label: "Statut de l'expédition", group: "shipment", type: "enum", options: opts(SHIPMENT_STATUSES, SHIPMENT_STATUS_LABELS) },
  { path: "shipment.carrierCode", label: "Transporteur", group: "shipment", type: "text" },
  { path: "shipment.attempts", label: "Tentatives de livraison", group: "shipment", type: "number" },
  { path: "product.name", label: "Nom du produit", group: "product", type: "text" },
  { path: "product.stock", label: "Stock du produit", group: "product", type: "number" },
  { path: "product.category", label: "Catégorie du produit", group: "product", type: "text" },
  {
    path: "event.to",
    label: "Nouveau statut",
    group: "event",
    type: "enum",
    options: [...opts(ORDER_STATUSES, ORDER_STATUS_LABELS), ...opts(SHIPMENT_STATUSES, SHIPMENT_STATUS_LABELS).filter((o) => !ORDER_STATUSES.includes(o.value as never))],
    triggers: ["order.status_changed", "shipment.status_changed"],
  },
  {
    path: "event.from",
    label: "Ancien statut",
    group: "event",
    type: "enum",
    options: [...opts(ORDER_STATUSES, ORDER_STATUS_LABELS), ...opts(SHIPMENT_STATUSES, SHIPMENT_STATUS_LABELS).filter((o) => !ORDER_STATUSES.includes(o.value as never))],
    triggers: ["order.status_changed", "shipment.status_changed"],
  },
  { path: "event.reason", label: "Motif d'annulation", group: "event", type: "enum", options: opts(CANCEL_REASONS, CANCEL_REASON_LABELS), triggers: ["order.cancelled"] },
  { path: "event.source", label: "Source", group: "event", type: "text", triggers: ["lead.created", "customer.created", "order.created"] },
  { path: "event.fromAd", label: "Venu d'une publicité", group: "event", type: "boolean", triggers: ["message.received", "conversation.created"] },
  { path: "event.stock", label: "Stock restant", group: "event", type: "number", triggers: ["product.low_stock"] },
  { path: "event.severity", label: "Gravité de l'alerte", group: "event", type: "text", triggers: ["ads.alert"] },
];

/** Fields the builder offers for a trigger. */
export function playbookFieldsFor(trigger: PlaybookTrigger): PlaybookFieldDef[] {
  const groups = new Set<string>(TRIGGER_ENTITIES[trigger]);
  return PLAYBOOK_FIELDS.filter((f) => groups.has(f.group) && (!f.triggers || f.triggers.includes(trigger)));
}

/** Operators that make sense for a field type. */
export function operatorsFor(type: PlaybookFieldType): ConditionOperator[] {
  switch (type) {
    case "number":
    case "money":
      return ["eq", "neq", "gt", "gte", "lt", "lte", "exists", "not_exists"];
    case "boolean":
      return ["eq", "neq"];
    case "enum":
      return ["eq", "neq", "in", "not_in", "exists", "not_exists"];
    case "tags":
      return ["contains", "not_contains", "exists", "not_exists"];
    default:
      return ["eq", "neq", "contains", "not_contains", "in", "not_in", "exists", "not_exists"];
  }
}

const channelParam = z.enum(FOLLOWUP_CHANNELS).optional().default("auto");

export const playbookActionParamsSchemas = {
  send_message: z.object({ text: z.string().trim().min(1, "Message requis").max(1000, "1 000 caractères maximum"), channel: channelParam }),
  send_template: z.object({ templateId: id, variables: z.array(z.string().trim().max(200)).max(20).optional().default([]) }),
  ai_reply: z.object({}).passthrough().transform(() => ({})),
  create_shipment: z.object({ carrierConnectionId: id.nullable().optional().default(null) }),
  set_order_status: z
    .object({
      status: z.enum(PLAYBOOK_ORDER_STATUSES),
      cancelReason: z.enum(CANCEL_REASONS).nullable().optional().default(null),
      note: z.string().trim().max(300).optional().default(""),
    })
    .refine((p) => p.status !== "cancelled" || Boolean(p.cancelReason), { message: "Motif d'annulation requis", path: ["cancelReason"] }),
  request_confirmation: z.object({}).passthrough().transform(() => ({})),
  add_tag: z.object({ target: z.enum(ACTION_TARGETS), tag: z.string().trim().min(1, "Tag requis").max(40, "40 caractères maximum") }),
  remove_tag: z.object({ target: z.enum(ACTION_TARGETS), tag: z.string().trim().min(1, "Tag requis").max(40, "40 caractères maximum") }),
  assign_conversation: z
    .object({ mode: z.enum(ASSIGN_MODES).optional().default("user"), userId: id.nullable().optional().default(null) })
    .refine((p) => p.mode !== "user" || Boolean(p.userId), { message: "Choisissez un membre", path: ["userId"] }),
  set_conversation_status: z.object({ status: z.enum(["open", "pending", "resolved"]) }),
  create_followup: z
    .object({
      delayMinutes: z.number().int().min(0).max(525_600),
      ruleId: id.nullable().optional().default(null),
      templateId: id.nullable().optional().default(null),
      messageBody: z.string().trim().max(1000).optional().default(""),
      category: z.enum(MESSAGE_CATEGORIES).optional().default("utility"),
    })
    .refine((p) => Boolean(p.ruleId) || Boolean(p.templateId) || Boolean(p.messageBody), { message: "Choisissez une règle, un modèle ou écrivez un message", path: ["messageBody"] }),
  notify_team: z.object({
    title: z.string().trim().min(1, "Titre requis").max(120),
    body: z.string().trim().max(500).optional().default(""),
    link: z.string().trim().max(300).optional().default(""),
  }),
  webhook: z.object({
    url: z
      .string()
      .trim()
      .url("URL invalide")
      .max(1000)
      .refine((u) => u.startsWith("https://"), "Le webhook doit utiliser https"),
    includeCustomer: z.boolean().optional().default(false),
  }),
  wait: z.object({
    minutes: z.number().int().min(1, "1 minute minimum").max(259_200, "180 jours maximum"),
    conditions: z.array(conditionInputSchema).max(10).optional().default([]),
  }),
} satisfies Record<PlaybookAction, z.ZodTypeAny>;

export type PlaybookActionParams = { [K in PlaybookAction]: z.infer<(typeof playbookActionParamsSchemas)[K]> };

const actionEntry = <K extends PlaybookAction>(type: K) =>
  z.object({ id: z.string().trim().max(40).optional(), type: z.literal(type), params: playbookActionParamsSchemas[type] });

export const playbookActionInputSchema = z.discriminatedUnion("type", [
  actionEntry("send_message"),
  actionEntry("send_template"),
  actionEntry("ai_reply"),
  actionEntry("create_shipment"),
  actionEntry("set_order_status"),
  actionEntry("request_confirmation"),
  actionEntry("add_tag"),
  actionEntry("remove_tag"),
  actionEntry("assign_conversation"),
  actionEntry("set_conversation_status"),
  actionEntry("create_followup"),
  actionEntry("notify_team"),
  actionEntry("webhook"),
  actionEntry("wait"),
]);
export type PlaybookActionInput = z.infer<typeof playbookActionInputSchema>;

export const playbookInputSchema = z.object({
  name: z.string().trim().min(2, "Nom trop court").max(120),
  description: z.string().trim().max(500).optional().default(""),
  enabled: z.boolean().optional().default(false),
  trigger: z.object({
    event: z.enum(PLAYBOOK_TRIGGERS, { errorMap: () => ({ message: "Déclencheur invalide" }) }),
    hour: z.number().int().min(0).max(23).optional().default(9),
  }),
  conditions: z.array(conditionInputSchema).max(15, "15 conditions maximum").optional().default([]),
  actions: z.array(playbookActionInputSchema).min(1, "Ajoutez au moins une action").max(15, "15 actions maximum"),
});
export type PlaybookInput = z.infer<typeof playbookInputSchema>;

export const playbookUpdateSchema = playbookInputSchema.partial();
export type PlaybookUpdate = z.infer<typeof playbookUpdateSchema>;

export const playbookToggleSchema = z.object({ enabled: z.boolean() });

export const playbookDryRunSchema = z.object({
  orderId: id.optional(),
  orderNumber: z.string().trim().max(40).optional(),
  conversationId: id.optional(),
  customerId: id.optional(),
  productId: id.optional(),
  /** Event fields for the test (event.to, event.reason...). */
  event: z.record(z.string().max(40), z.union([z.string().max(200), z.number(), z.boolean(), z.null()])).optional().default({}),
});
export type PlaybookDryRunInput = z.infer<typeof playbookDryRunSchema>;

export const playbookRunListQuery = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(20),
  status: z.enum([...PLAYBOOK_RUN_STATUSES, "all"] as const).default("all"),
});
export type PlaybookRunListQuery = z.infer<typeof playbookRunListQuery>;

export interface PlaybookDto {
  id: string;
  name: string;
  description: string;
  enabled: boolean;
  trigger: { event: PlaybookTrigger; hour: number };
  conditions: ConditionInput[];
  actions: PlaybookActionInput[];
  templateKey: string | null;
  hasWebhookSecret: boolean;
  stats: { runs: number; successes: number; failures: number; lastRunAt: string | null; waiting: number };
  lastError: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface PlaybookRunStepDto {
  actionId: string;
  type: string;
  status: "completed" | "failed" | "skipped";
  at: string;
  error: string | null;
  output: Record<string, unknown> | null;
}

export interface PlaybookRunDto {
  id: string;
  playbookId: string;
  eventType: string;
  status: PlaybookRunStatus;
  nextActionIndex: number;
  resumeAt: string | null;
  orderId: string | null;
  orderNumber: string | null;
  customerId: string | null;
  customerName: string | null;
  conversationId: string | null;
  error: string | null;
  steps: PlaybookRunStepDto[];
  createdAt: string;
  completedAt: string | null;
}

export interface PlaybookTemplateDto {
  key: string;
  name: string;
  description: string;
  trigger: { event: PlaybookTrigger; hour: number };
  conditions: ConditionInput[];
  actions: PlaybookActionInput[];
  used: boolean;
}

export interface ConditionEvaluationDto {
  field: string;
  label: string;
  op: ConditionOperator;
  expected: unknown;
  actual: unknown;
  passed: boolean;
}

export interface PlaybookDryRunResultDto {
  matched: boolean;
  context: { orderId: string | null; orderNumber: string | null; customerId: string | null; customerName: string | null; conversationId: string | null; productId: string | null };
  conditions: ConditionEvaluationDto[];
  actions: Array<{ id: string; type: PlaybookAction; label: string; wouldRun: boolean; preview: string; note: string | null }>;
  notes: string[];
}

export interface PlaybookOptionsDto {
  templates: ApprovedTemplateDto[];
  members: Array<{ id: string; name: string; role: string }>;
  carriers: Array<{ id: string; name: string; carrierCode: string }>;
  rules: Array<{ id: string; name: string; type: FollowupRuleType }>;
  currency: string;
}
